import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { DocumentViewer } from '~/features/document-viewer/DocumentViewer';
import { DocumentVersion } from '~/features/document/types/documentVersion';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

interface Props {
  onHide: () => void;
  versions: DocumentVersion[];
  selectedVersion: DocumentVersion;
}
export const DocumentVersionViewer = ({
  selectedVersion: initialSelectedVersion,
  versions,
  onHide,
}: Props) => {
  const [selectedVersion, setSelectedVersion] = useState(
    initialSelectedVersion,
  );
  const intl = useIntl();
  const formatDate = useFormatDate();

  const versionOptions = useMemo(() => {
    return versions.map((version) => ({
      value: version.id,
      label: intl.formatMessage(
        {
          id: 'DocumentVersionViewer.versionLabel',
          defaultMessage: 'Version {versionTag}',
          description:
            'Label for version option in dropdown. Example: versionTag = "v1.2"',
        },
        {
          versionTag: version.version_tag,
        },
      ),
    }));
  }, [versions]);

  const handleVersionChange = (versionId: Nullable<PilotId>) => {
    if (versionId) {
      const version = versions.find((version) => version.id === versionId);
      if (version) {
        setSelectedVersion(version);
      }
    }
  };

  return (
    <Modal isVisible title={selectedVersion.file_name} onHide={onHide}>
      <Layout overflowY="hidden" direction="column" id="test">
        <Layout direction="column" spacing={2}>
          {!selectedVersion.is_latest && (
            <StatusMessage
              status="info"
              message={intl.formatMessage(
                {
                  id: 'DocumentVersionViewer.oldVersionMessage',
                  defaultMessage:
                    'You are viewing an older version of this document. This is version {versionTag}, last modified on {dateModified} by {lastModifiedBy}.',
                  description:
                    'Message shown when viewing an older document version. Example: versionTag = "v1.2", dateModified = "2023-12-01", lastModifiedBy = "John Smith"',
                },
                {
                  versionTag: selectedVersion.version_tag,
                  dateModified: formatDate(
                    selectedVersion.date_modified,
                    'short_datetime',
                  ),
                  lastModifiedBy: selectedVersion.last_modified_by,
                },
              )}
            />
          )}
          <Layout alignSelf="flex-end">
            <Select<number, false>
              name="version-select"
              isMulti={false}
              isSearchable={false}
              options={versionOptions}
              value={selectedVersion.id}
              onChange={handleVersionChange}
            />
          </Layout>
        </Layout>
        <Box overflowY="auto" minH={600}>
          <DocumentViewer
            documentVersionId={selectedVersion.id}
            options={{
              autoLatestVersion: false,
            }}
          />
        </Box>
      </Layout>
    </Modal>
  );
};
